import { TicketCategory, TicketPriority } from '@/Modules/HelpdeskModule/shared/types';
import type { CapturedContext } from '@/shared/stores/bug-report.store';

const POLLING_PATTERN = /helpdesk\/(analytics|notifications)/;
const SKIP_STATUSES = new Set([400, 401, 403, 404]);

export function shouldCapture(endpoint: string, status: number | null): boolean {
  if (status === null) return true; // network / timeout
  if (SKIP_STATUSES.has(status)) return false; // auth / not-found
  if (status < 400) return false; // success / redirect
  if (POLLING_PATTERN.test(endpoint)) return false; // background polling
  return true;
}

export function mapStatusToPriority(status: number | null): TicketPriority {
  if (status === null || status >= 500) return TicketPriority.HIGH;
  return TicketPriority.MEDIUM;
}

export function buildDedupKey(method: string, endpoint: string, status: number | null): string {
  return `${method.toUpperCase()}:${endpoint}:${status ?? 'network'}`;
}

export function formatStepsToReproduce(ctx: CapturedContext): string {
  const lines: string[] = [
    '--- Auto-captured context (for developers) ---',
    `Route: ${ctx.route}`,
    `Timestamp: ${ctx.failedCall.timestamp}`,
    '',
    'Failed request:',
    `  ${ctx.failedCall.method} ${ctx.failedCall.endpoint} → ${
      ctx.failedCall.status ?? 'NETWORK_ERROR'
    }`,
    '',
    `Session ID: ${ctx.sessionId || 'n/a'}`,
    `User ID: ${ctx.userId || 'n/a'}`
  ];

  if (ctx.recentFailures.length > 1) {
    lines.push('', 'Recent failures:');
    ctx.recentFailures.forEach((r) => {
      const isCurrent =
        r.endpoint === ctx.failedCall.endpoint && r.timestamp === ctx.failedCall.timestamp;
      lines.push(
        `  ${r.method.padEnd(6)} ${r.endpoint} → ${r.status ?? 'NETWORK_ERROR'}  (${r.timestamp})${
          isCurrent ? '  ← this report' : ''
        }`
      );
    });
  }

  lines.push('', '--- Your steps to reproduce ---', '');
  return lines.join('\n');
}

export function buildPrefill(ctx: CapturedContext) {
  const { failedCall } = ctx;
  return {
    title: `[Bug] ${failedCall.method} ${failedCall.endpoint} → ${
      failedCall.status ?? 'NETWORK_ERROR'
    }`,
    category: TicketCategory.BUG,
    priority: mapStatusToPriority(failedCall.status),
    stepsToReproduce: formatStepsToReproduce(ctx)
  };
}

export function formatMinimalContext(opts: {
  route: string;
  sessionId: string;
  userId: string;
}): string {
  return [
    '--- Auto-captured context (for developers) ---',
    `Route: ${opts.route}`,
    `Session ID: ${opts.sessionId || 'n/a'}`,
    `User ID: ${opts.userId || 'n/a'}`,
    '',
    '--- Your steps to reproduce ---',
    ''
  ].join('\n');
}
